/**
 * External-publishing integrations (News Watch Studio → distribution).
 *
 * Credentials live in a dedicated DDB item, NEVER on the brand row (which is
 * returned wholesale to the client). Only the connection STATUS (below) is ever
 * sent to the frontend — the secret (WordPress application password) stays
 * server-side.
 */

/** Per-brand WordPress connection. One WP site per brand (an agency's client
 *  each has their own site). Uses WordPress Application Passwords (revocable,
 *  scoped — NOT the account password). */
export interface WordPressConnection {
  siteUrl: string;
  username: string;
  /** Application password — server-side only, never serialized to the client. */
  applicationPassword: string;
  connectedAt: string;
}

/** Client-safe view of the WP connection — no secret. */
export interface WordPressConnectionStatus {
  connected: boolean;
  siteUrl?: string;
  username?: string;
  connectedAt?: string;
}

/** A WordPress taxonomy term (category or tag) for the publish dialog. */
export interface WordPressTerm {
  id: number;
  name: string;
}

/** Result of publishing an article to WordPress. */
export interface WordPressPublishResult {
  postId: number;
  postUrl: string;
  status: 'draft' | 'publish';
}

// ============================================================
// Generic outbound webhooks (Studio content → any endpoint)
// ============================================================

export type WebhookEvent = 'article.generated' | 'article.published' | 'article.dismissed';

export const WEBHOOK_EVENTS: WebhookEvent[] = [
  'article.generated',
  'article.published',
  'article.dismissed',
];

/** Stored webhook config. The `secret` is server-side only — used to HMAC-sign
 *  payloads; NEVER returned to the client after creation. */
export interface WebhookConfig {
  id: string;
  url: string;
  secret: string;
  events: WebhookEvent[];
  active: boolean;
  createdAt: string;
}

/** Client-safe view — secret redacted to its last 4 chars. */
export interface WebhookConfigView {
  id: string;
  url: string;
  secretHint: string; // e.g. "••••ab12"
  events: WebhookEvent[];
  active: boolean;
  createdAt: string;
}

/** One delivery attempt, for the per-webhook delivery log. */
export interface WebhookDelivery {
  webhookId: string;
  event: WebhookEvent;
  status: 'success' | 'failed';
  responseCode?: number;
  attempts: number;
  error?: string;
  deliveredAt: string;
}

/** A channel variant in a webhook payload (flattened, subscriber-friendly). */
export interface WebhookVariant {
  channel: string;
  kind: string;
  /** Plain text for single-string variants; JSON string for structured (thread/script). */
  text: string;
}

/**
 * The JSON body POSTed to a subscriber (signed via `X-Traaker-Signature`).
 *
 * Self-contained by design — carries the FULL article + all channel variants so
 * the subscriber's automation (Make/n8n/Zapier → Slack/Notion/CMS) can act with
 * no fetch-back and no extra auth. Payload is a few KB; HMAC-signed for authenticity.
 */
export interface WebhookPayload {
  event: WebhookEvent;
  tenantId: string;
  brandId: string;
  articleId: string;
  occurredAt: string;
  data: {
    title: string;
    subtitle?: string;
    /** Full article body (markdown). */
    articleMarkdown?: string;
    /** All generated channel variants (announcement, thread, captions…). */
    variants?: WebhookVariant[];
    targetGeoQuery?: string;
    topicTags?: string[];
    status?: string;
    publishedUrl?: string;
    channels?: string[];
    editorUrl?: string;
  };
}
